import {
    COUNTRY_CATEGORIES,
    POLICY_OPTIONS
} from '../../../../../components/prosemirror-editor/components/documentHeader/disclosureFieldOptions';

// Container types that should not be extracted as blocks themselves
// (we want their nested content blocks instead)
const CONTAINER_TYPES = new Set([
    'ordered_list',
    'bullet_list',
    'list_item',
    'table',
    'table_row',
    'table_cell',
    'table_header',
    'blockquote',
    'image',
    'agenticSection',
]);

const EXTRACTABLE_BLOCK_TYPES = new Set(['paragraph', 'heading', 'code_block']);

/**
 * Normalize API/download payloads to a ProseMirror doc node.
 */
export function normalizePmDoc(payload) {
    if (!payload || typeof payload !== 'object') return null;
    if (payload.type === 'doc' && Array.isArray(payload.content)) {
        return payload;
    }
    if (payload.pmJson?.type === 'doc' && Array.isArray(payload.pmJson.content)) {
        return payload.pmJson;
    }
    // downloadLatestVersionFromApi wraps the PM doc as `{content, references, metadata}`.
    if (payload.content?.type === 'doc' && Array.isArray(payload.content.content)) {
        return payload.content;
    }
    return null;
}

export function getCountryCategory(country) {
    if (!country) return '';

    for (const categoryGroup of COUNTRY_CATEGORIES) {
        if (categoryGroup.countries.includes(country)) {
            return categoryGroup.category;
        }
    }

    return 'unknown';
}

function extractTextContent(node) {
    if (!node) return '';
    if (node.type === 'text') return node.text || '';
    if (Array.isArray(node.content)) return node.content.map(extractTextContent).join('');
    return '';
}

export function extractBlocksFromPMJson(pmJson) {
    const doc = normalizePmDoc(pmJson);
    if (!doc) return [];

    const blocks = [];
    const seen = new Set();

    function pushBlock(node, text) {
        const trimmed = text.trim();
        if (!trimmed) return;

        const blockKey = node?.attrs?.blockId
            || node?.attrs?.uid
            || `${node?.type || 'block'}:${trimmed.slice(0, 120)}`;
        if (seen.has(blockKey)) return;
        seen.add(blockKey);

        const element = node?.type === 'heading' ? 'heading' : 'paragraph';
        const classification = node?.attrs?.classificationLevel || 'U';
        blocks.push([classification, element, trimmed]);
    }

    function walk(node) {
        if (!node) return;

        if (node.type === 'agenticSection') {
            const responseText = node.attrs?.responseText || '';
            pushBlock({type: 'paragraph', attrs: {classificationLevel: 'U'}}, responseText);
            return;
        }

        if (CONTAINER_TYPES.has(node.type)) {
            if (Array.isArray(node.content)) {
                node.content.forEach(walk);
            }
            return;
        }

        if (EXTRACTABLE_BLOCK_TYPES.has(node.type)) {
            pushBlock(node, extractTextContent(node));
        }

        if (Array.isArray(node.content)) {
            node.content.forEach(walk);
        }
    }

    doc.content.forEach(walk);
    return blocks;
}

export function getDisclosurePolicyDescription(selectedPolicy) {
    const policyOption = POLICY_OPTIONS.find(p => p.value === selectedPolicy);
    return policyOption?.description || '';
}
